import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { AdminPanelSettings, Style } from '@mui/icons-material';
import { adminService, AdminUserDto, UpdateAccessRequest } from '../services/adminService';
import AppNavigation from './AppNavigation';
import AdminReportsTab from './AdminReportsTab';
import AdminMediaTab from './AdminMediaTab';

interface AdminDeck {
  deckId: string;
  deckName: string;
}

const AdminPanel: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [editUser, setEditUser] = useState<AdminUserDto | null>(null);
  const [editForm, setEditForm] = useState<UpdateAccessRequest | null>(null);
  const [saving, setSaving] = useState(false);
  const [allDecks, setAllDecks] = useState<AdminDeck[]>([]);
  const [deckModalOpen, setDeckModalOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState<AdminUserDto | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUserDto | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<AdminUserDto | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadUsers = useCallback(async (reset: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.getUsers({
        search: search || undefined,
        pageToken: reset ? undefined : nextPageToken,
        pageSize: 50,
      });
      setUsers(prev => reset ? result.users : [...prev, ...result.users]);
      setNextPageToken(result.nextPageToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, nextPageToken]);

  useEffect(() => {
    loadUsers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    adminService.getDecks()
      .then(setAllDecks)
      .catch(() => setAllDecks([]));
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const openEditDialog = (user: AdminUserDto) => {
    setEditUser(user);
    setEditForm({
      hasChatAccess: user.hasChatAccess,
      hasFlashcardAccess: user.hasFlashcardAccess,
      hasApneaAccess: user.hasApneaAccess,
      allowedDeckIds: [...user.allowedDeckIds],
      isAdmin: user.isAdmin,
    });
  };

  const handleSave = async () => {
    if (!editUser || !editForm) return;
    setSaving(true);
    try {
      const updated = await adminService.updateAccess(editUser.userId, editForm);
      setUsers(prev => prev.map(u => u.userId === updated.userId ? updated : u));
      setEditUser(null);
      setEditForm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordDialog = (user: AdminUserDto) => {
    setPasswordUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  const closePasswordDialog = () => {
    setPasswordUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  const handleSetPassword = async () => {
    if (!passwordUser) return;
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordSaving(true);
    setPasswordError(null);
    try {
      await adminService.setPassword(passwordUser.userId, newPassword);
      setPasswordSuccess(`Password updated for ${passwordUser.firstName} ${passwordUser.lastName}`);
      closePasswordDialog();
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to set password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const openDeleteDialog = (user: AdminUserDto) => {
    setDeleteTarget(user);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const deleted = await adminService.deleteUser(deleteTarget.userId);
      const totalItems = Object.values(deleted).reduce((sum, n) => sum + n, 0);
      setUsers(prev => prev.filter(u => u.userId !== deleteTarget.userId));
      setPasswordSuccess(`Deleted ${deleteTarget.firstName} ${deleteTarget.lastName} and ${totalItems} related record(s)`);
      closeDeleteDialog();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleDisconnectSuunto = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await adminService.disconnectSuunto(disconnectTarget.userId);
      setUsers(prev => prev.map(u =>
        u.userId === disconnectTarget.userId ? { ...u, hasSuuntoConnection: false } : u
      ));
      setPasswordSuccess(`Cleared the Suunto connection for ${disconnectTarget.firstName} ${disconnectTarget.lastName}`);
      setDisconnectTarget(null);
    } catch (err) {
      setDisconnectError(err instanceof Error ? err.message : 'Failed to disconnect Suunto');
    } finally {
      setDisconnecting(false);
    }
  };

  const toggleDeck = (deckId: string) => {
    if (!editForm) return;
    const current = editForm.allowedDeckIds;
    const next = current.includes(deckId)
      ? current.filter(id => id !== deckId)
      : [...current, deckId];
    setEditForm({ ...editForm, allowedDeckIds: next });
  };

  const deckLabel = (ids: string[]) => {
    if (ids.length === 0) return 'All decks';
    const names = ids.map(id => allDecks.find(d => d.deckId === id)?.deckName ?? id);
    return names.join(', ');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <AppNavigation />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AdminPanelSettings />
        <Typography variant="h5">Admin</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Users" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Reports" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Media" sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {tab === 2 ? (
        <AdminMediaTab />
      ) : tab === 1 ? (
        <AdminReportsTab />
      ) : (
        <>
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name or email"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <Button type="submit" variant="contained" disabled={loading}>Search</Button>
        {search && (
          <Button onClick={() => { setSearch(''); setSearchInput(''); }}>Clear</Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Chat</TableCell>
              <TableCell align="center">Flashcards</TableCell>
              <TableCell align="center">Apnea</TableCell>
              <TableCell align="center">Admin</TableCell>
              <TableCell align="center">Deck Restrictions</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.userId} hover>
                <TableCell>{user.firstName} {user.lastName}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={user.hasChatAccess ? 'Yes' : 'No'}
                    color={user.hasChatAccess ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={user.hasFlashcardAccess ? 'Yes' : 'No'}
                    color={user.hasFlashcardAccess ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={user.hasApneaAccess ? 'Yes' : 'No'}
                    color={user.hasApneaAccess ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {user.isAdmin && <Chip label="Admin" color="warning" size="small" />}
                </TableCell>
                <TableCell align="center">
                  {user.allowedDeckIds.length === 0
                    ? <Typography variant="caption" color="text.secondary">All decks</Typography>
                    : <Typography variant="caption">{user.allowedDeckIds.length} deck(s)</Typography>
                  }
                </TableCell>
                <TableCell align="center">
                  <Button size="small" onClick={() => openEditDialog(user)}>Edit</Button>
                  <Button size="small" onClick={() => openPasswordDialog(user)}>Reset Password</Button>
                  {user.hasSuuntoConnection && (
                    <Button
                      size="small"
                      onClick={() => { setDisconnectTarget(user); setDisconnectError(null); }}
                    >
                      Disconnect Suunto
                    </Button>
                  )}
                  <Button size="small" color="error" onClick={() => openDeleteDialog(user)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">No users found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress /></Box>}

      {nextPageToken && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={() => loadUsers(false)}>Load More</Button>
        </Box>
      )}
        </>
      )}

      {/* Edit Access Dialog */}
      <Dialog open={!!editUser} onClose={() => { setEditUser(null); setEditForm(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Access — {editUser?.firstName} {editUser?.lastName}</DialogTitle>
        <DialogContent>
          {editForm && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.hasChatAccess}
                    onChange={e => setEditForm({ ...editForm, hasChatAccess: e.target.checked })}
                  />
                }
                label="Nereus AI Chat access"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.hasFlashcardAccess}
                    onChange={e => setEditForm({ ...editForm, hasFlashcardAccess: e.target.checked })}
                  />
                }
                label="Flashcard access"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.hasApneaAccess}
                    onChange={e => setEditForm({ ...editForm, hasApneaAccess: e.target.checked })}
                  />
                }
                label="Apnea training access"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isAdmin}
                    onChange={e => setEditForm({ ...editForm, isAdmin: e.target.checked })}
                  />
                }
                label="Admin access"
              />

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Allowed decks: {editForm.allowedDeckIds.length === 0 ? 'All decks' : `${editForm.allowedDeckIds.length} selected`}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Style />}
                    onClick={() => setDeckModalOpen(true)}
                    variant="outlined"
                  >
                    Edit Decks
                  </Button>
                </Box>
                {editForm.allowedDeckIds.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {deckLabel(editForm.allowedDeckIds)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditUser(null); setEditForm(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!passwordUser} onClose={closePasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password — {passwordUser?.firstName} {passwordUser?.lastName}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {passwordError && <Alert severity="error">{passwordError}</Alert>}
            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              helperText="At least 8 characters"
              fullWidth
            />
            <TextField
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              error={confirmPassword.length > 0 && confirmPassword !== newPassword}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              The user's existing sessions stay signed in; only future logins use the new password.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSetPassword}
            disabled={passwordSaving || newPassword.length < 8 || newPassword !== confirmPassword}
          >
            {passwordSaving ? <CircularProgress size={18} /> : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={!!deleteTarget} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User — {deleteTarget?.firstName} {deleteTarget?.lastName}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <Alert severity="warning">
              This permanently deletes the account and ALL of the user's data: dives, locations,
              equipment, scheduled dives, buddy connections, notifications, chat history,
              dive site lists, preferences, and access settings. This cannot be undone.
            </Alert>
            <TextField
              label={`Type ${deleteTarget?.email ?? ''} to confirm`}
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              autoComplete="off"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={deleting || deleteConfirmText.trim().toLowerCase() !== (deleteTarget?.email ?? '').toLowerCase()}
          >
            {deleting ? <CircularProgress size={18} /> : 'Delete Everything'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect Suunto Dialog */}
      <Dialog open={!!disconnectTarget} onClose={() => setDisconnectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Disconnect Suunto — {disconnectTarget?.firstName} {disconnectTarget?.lastName}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {disconnectError && <Alert severity="error">{disconnectError}</Alert>}
            <Alert severity="info">
              This clears the stored Suunto tokens so the user is prompted to connect again.
              Dives already imported are kept — only the Suunto link is removed.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectTarget(null)} disabled={disconnecting}>Cancel</Button>
          <Button variant="contained" onClick={handleDisconnectSuunto} disabled={disconnecting}>
            {disconnecting ? <CircularProgress size={18} /> : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!passwordSuccess}
        autoHideDuration={4000}
        onClose={() => setPasswordSuccess(null)}
        message={passwordSuccess}
      />

      {/* Deck selection modal */}
      <Dialog open={deckModalOpen} onClose={() => setDeckModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Allow Decks</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              Check decks to allow. Uncheck all to allow every deck.
            </Typography>
            <Button
              size="small"
              onClick={() => editForm && setEditForm({ ...editForm, allowedDeckIds: [] })}
            >
              Allow All
            </Button>
          </Box>
          {allDecks.length === 0 ? (
            <Typography sx={{ p: 2 }} color="text.secondary">No decks available.</Typography>
          ) : (
            <List dense disablePadding>
              {allDecks.map(deck => (
                <ListItem key={deck.deckId} disablePadding>
                  <ListItemButton onClick={() => toggleDeck(deck.deckId)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={editForm?.allowedDeckIds.includes(deck.deckId) ?? false}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText primary={deck.deckName} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeckModalOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPanel;
